'use client'

import { useEffect, useSyncExternalStore } from 'react'

/**
 * The record page's own breadcrumb label (sandbox QA defect 2). The header
 * builds the trail from the URL, which only knows the record id; a record page
 * calls `useSetRecordCrumb(path, name)` once its data has loaded and the header
 * reads it with `useRecordCrumb(pathname)`. The label is keyed by path, so a
 * name never carries over to another page, and cleared when the page unmounts.
 */
type Entry = { path: string; label: string } | null

let entry: Entry = null
const listeners = new Set<() => void>()

function emit(next: Entry) {
  entry = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useRecordCrumb(pathname: string | null | undefined): string | null {
  const current = useSyncExternalStore(subscribe, () => entry, () => null)
  return current && current.path === pathname ? current.label : null
}

export function useSetRecordCrumb(path: string, label: string | null | undefined) {
  useEffect(() => {
    if (!label) return
    emit({ path, label })
    return () => {
      if (entry?.path === path) emit(null)
    }
  }, [path, label])
}
